import { v4 as uuid } from 'uuid'
import api from '@/api'
import moment from 'moment'
import download from '@/helpers/download'
import { hasCyclicDependencies } from '@/helpers/bone'
import { parse, stringify } from '@/helpers/crop'
import { setFlag, clearFlag } from '@/helpers/flags'
import range from '@/helpers/range'

export default {
  namespaced: true,
  state: {
    locations: [],
    location: null,
    types: [],
    type: null,
    MainYield: 2.5,
    seedingDate: null,
    harvestDate: null,
    data: null,
    version: 0,
    showAdvancedSettings: false
  },
  mutations: {
    setData (state, data) {
      state.data = data
    },
    setLocations (state, locations) {
      if (!Array.isArray(locations)) {
        throw new Error('Invalid locations')
      }
      state.locations = locations
    },
    setLocation (state, location) {
      if (!state.locations.includes(location)) {
        throw new Error(`Invalid location "${location}"`)
      }
      state.location = location
    },
    setMainYield (state, MainYield) {
      state.MainYield = MainYield
    },
    setTypes (state, types) {
      if (!Array.isArray(types)) {
        throw new Error('Invalid types')
      }
      state.types = types
    },
    setType (state, type) {
      const found = state.types.find((current) => current.id === type)
      if (!found) {
        throw new Error(`Invalid type "${type}"`)
      }
      const seedingDate = moment(`${moment().year()}`, 'YYYY').dayOfYear(found.seedingDayOfYear).toDate()
      const harvestDate = moment(seedingDate).add(found.growthPeriod, 'days').toDate()
      state.type = found
      state.seedingDate = seedingDate
      state.harvestDate = harvestDate
    },
    setSeedingDate (state, seedingDate) {
      if (!(seedingDate instanceof Date)) {
        throw new Error('Invalid seeding date')
      }
      state.seedingDate = seedingDate
    },
    setHarvestDate (state, harvestDate) {
      if (!(harvestDate instanceof Date)) {
        throw new Error('Invalid harvest date')
      }
      state.harvestDate = harvestDate
    },

    /* Bone editing functions */
    createBone (state) {
      const [x, y] = [0, 0]
      state.data.bones.push({
        id: uuid(),
        name: 'Unnamed',
        parent,
        start: [x, y],
        end: [x, y],
        currentStart: [x, y],
        currentEnd: [x, y],
        currentLength: 0,
        currentWidth: 10,
        currentOrientation: 0,
        direction: [0, 0],
        orientation: 0,
        width: 10,
        length: 0,
        flags: 0,
        BBCH: [0, 99],
        image: {
          defaultValue: '',
          keys: [
            [0, {
              value: ''
            }]
          ]
        }
      })
    },
    createBoneStartAt (state, { position: [x, y], parent, useLastParent }) {
      if (useLastParent === true) {
        parent = state.data.bones.length - 1
      }
      state.data.bones.push({
        id: uuid(),
        name: 'Unnamed',
        parent,
        start: [x, y],
        end: [x, y],
        currentStart: [x, y],
        currentEnd: [x, y],
        currentLength: 0,
        currentWidth: 10,
        currentOrientation: 0,
        direction: [0, 0],
        orientation: 0,
        width: 10,
        length: 0,
        flags: 0,
        BBCH: [0, 99],
        image: {
          defaultValue: '',
          keys: [[0, {
            value: ''
          }]]
        }
      })
    },
    updateLastBoneEnd (state, { position }) {
      state.data.bones[state.data.bones.length - 1].end = position
    },
    moveBonePointsBy (state, { move: [x, y], points }) {
      for (const { index, joint } of points) {
        const [oldX, oldY] = state.data.bones[index][joint]
        state.data.bones[index][joint] = [oldX + x, oldY + y]
      }
      state.version++
    },
    addBone (state, newBone) {
      state.data.bones.push(newBone)
      state.version++
    },
    removeBonesAt (state, { indices }) {
      const reversedIndices = indices.sort().reverse()
      for (const index of reversedIndices) {
        state.data.bones.splice(index, 1)
      }
      state.version++
    },
    removeBoneAt (state, { index }) {
      state.data.bones.splice(index, 1)
      for (let i = state.data.bones.length - 1; i >= index; i--) {
        if (state.data.bones[i].parent === index) {
          console.warn(`${state.data.bones[i].name} had parent ${index}`)
          state.data.bones[i].parent = -1
        } else if (state.data.bones[i].parent > 0) {
          state.data.bones[i].parent--
        }
      }
      state.version++
    },
    setBoneName (state, { index, name }) {
      state.data.bones[index].name = name
      state.version++
    },
    setBoneStart (state, { index, start }) {
      state.data.bones[index].start = start
      state.version++
    },
    setBoneStartX (state, { index, x }) {
      const [, y] = state.data.bones[index].start
      state.data.bones[index].start = [x, y]
      state.version++
    },
    setBoneStartY (state, { index, y }) {
      const [x] = state.data.bones[index].start
      state.data.bones[index].start = [x, y]
      state.version++
    },
    setBoneEnd (state, { index, end }) {
      state.data.bones[index].end = end
      state.version++
    },
    setBoneEndX (state, { index, x }) {
      const [, y] = state.data.bones[index].end
      state.data.bones[index].end = [x, y]
      state.version++
    },
    setBoneEndY (state, { index, y }) {
      const [x] = state.data.bones[index].end
      state.data.bones[index].end = [x, y]
      state.version++
    },
    setBoneParent (state, { index, parent }) {
      const oldParent = state.data.bones[index].parent
      state.data.bones[index].parent = parent
      // If we detect a cyclic dependency then we restore the old parent.
      if (hasCyclicDependencies(state.data.bones[index], index, state.data.bones)) {
        state.data.bones[index].parent = oldParent
      }
      state.version++
    },
    setBoneWidth (state, { index, width }) {
      state.data.bones[index].width = width
      state.version++
    },
    setBoneLength (state, { index, length }) {
      state.data.bones[index].length = length
      state.version++
    },
    setBoneBBCH (state, { index, min, max }) {
      const BBCH = state.data.bones[index].BBCH
      if (min === undefined) {
        min = BBCH[0]
      }
      if (max === undefined) {
        max = BBCH[1]
      }
      state.data.bones[index].BBCH = [min, max]
      state.version++
    },
    setBoneFlag (state, { index, flag, checked }) {
      const oldFlags = state.data.bones[index].flags
      const newFlags = (checked) ? setFlag(oldFlags, flag) : clearFlag(oldFlags, flag)
      state.data.bones[index].flags = newFlags
      state.version++
    },
    addBoneImage (state, { index }) {
      state.data.bones[index].image.keys.push([
        99, { value: '' }
      ])
      state.version++
    },
    removeBoneImage (state, { index, imageIndex }) {
      state.data.bones[index].image.keys.splice(imageIndex, 1)
      state.version++
    },
    setBoneImageBBCH (state, { index, imageIndex, BBCH }) {
      const { value } = state.data.bones[index].image.keys[imageIndex][1]
      state.data.bones[index].image.keys[imageIndex] = [
        range.clamp(BBCH, 0, 99),
        { value }
      ]
      state.version++
    },
    setBoneImageName (state, { index, imageIndex, value }) {
      const BBCH = state.data.bones[index].image.keys[imageIndex][0]
      state.data.bones[index].image.keys[imageIndex] = [
        BBCH,
        { value }
      ]
      state.version++
    },

    setNumRoots (state, numRoots) {
      state.data.numRoots = numRoots
      state.version++
    },
    createRoot (state) {
      state.data.roots.push({
        id: uuid(),
        name: 'Unnamed root',
        numPoints: 15,
        BBCH: [0, 99],
        length: [1, 1],
        angle: [-0.1, 0.1],
        bend: 0.1,
        noise: 0.05
      })
      state.version++
    },
    removeRootAt (state, { index }) {
      state.data.roots.splice(index, 1)
      state.version++
    },
    setRootName (state, { index, name }) {
      state.data.roots[index].name = name
      state.version++
    },
    setRootBBCH (state, { index, min, max }) {
      const BBCH = state.data.roots[index].BBCH
      if (min === undefined) {
        min = BBCH[0]
      }
      if (max === undefined) {
        max = BBCH[1]
      }
      state.data.roots[index].BBCH = [min, max]
      state.version++
    },
    setRootSeed (state, { index, seed }) {
      state.data.roots[index].seed = seed
      state.version++
    },
    setRootPoints (state, { index, numPoints }) {
      state.data.roots[index].numPoints = numPoints
      state.version++
    },
    setRootLength (state, { index, min, max }) {
      const length = state.data.roots[index].length
      if (min === undefined) {
        min = length[0]
      }
      if (max === undefined) {
        max = length[1]
      }
      state.data.roots[index].length = [min, max]
      state.version++
    },
    setRootAngle (state, { index, min, max }) {
      const angle = state.data.roots[index].angle
      if (min === undefined) {
        min = angle[0]
      }
      if (max === undefined) {
        max = angle[1]
      }
      state.data.roots[index].angle = [min, max]
      state.version++
    },
    setRootBend (state, { index, bend }) {
      state.data.roots[index].bend = bend
      state.version++
    },
    setRootNoise (state, { index, noise }) {
      state.data.roots[index].noise = noise
      state.version++
    },

    setDebugOption (state, { name, checked }) {
      switch (name) {
        case 'renderImageName':
          state.data.debug.renderImageName = checked
          break
        case 'renderImageRect':
          state.data.debug.renderImageRect = checked
          break
        case 'renderCurrentBone':
          state.data.debug.renderCurrentBone = checked
          break
        case 'renderBone':
          state.data.debug.renderBone = checked
          break
      }
      state.version++
    },

    addImage (state, { name, image }) {
      state.data.images.set(name, image)
      state.version++
    },
    addImages (state, { images }) {
      for (const [name, image] of images) {
        state.data.images.set(name, image)
      }
      state.version++
    },
    renameImage (state, { oldName, newName }) {
      const image = state.data.images.get(oldName)
      state.data.images.set(newName, image)
      state.data.images.delete(oldName)
    },
    removeImage (state, name) {
      state.data.images.delete(name)
      state.version++
    }
  },
  actions: {
    async loadTypes ({ commit }, region) {
      const types = await api.loadCropTypes(region)
      commit('setTypes', types)
    },
    async loadData ({ commit }, crop) {
      const data = await api.loadCrop(crop.file)
      const images = await api.loadCropImages(data.images)
      const cropData = parse(data, images)
      commit('setData', cropData)
      return cropData
    },
    saveData ({ state }) {
      const blob = new Blob([
        stringify(state.data)
      ], { type: 'application/json' })
      const filename = prompt('Filename')
      if (filename) {
        download.fromBlob(blob, filename)
      }
    }
  }
}
